import {
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import type { SOPMappingDTO } from "@/modules/sop/domain/contracts/mapping";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type SOPMappingRow = SOPMappingDTO;

type SOPMappingTableProps = {
  data: SOPMappingRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (mapping: SOPMappingRow) => void;
  onDelete: (mapping: SOPMappingRow) => void;
};

export function SOPMappingTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: SOPMappingTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<SOPMappingRow>[] = [
    createSortableColumn<SOPMappingRow>("sopTitle", "ຊື່ SOP", { size: 200 }),
    createSortableColumn<SOPMappingRow>("positionTitle", "ຕໍາແໜ່ງ", {
      size: 200,
    }),
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "createdAt",
      header: "ວັນທີສ້າງ",
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
      size: 120,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: SOPMappingRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive";
        }[] = [];

        if (has(Permissions.sop.update)) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.sop.delete)) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບການຜູກ SOP",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການຜູກ SOP "${row.original.sopTitle}" ກັບຕໍາແໜ່ງ "${row.original.positionTitle}"?`,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(row.original);
              }
            },
          });
        }

        return <RowActions actions={actions} />;
      },
    },
  ];

  return (
    <DataTable<SOPMappingRow, unknown>
      noDataMessage="ບໍ່ພົບການຜູກ SOP"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
